import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import payrollService from '../../services/payrollService';
import { AlertCircle, IndianRupee, Users, CheckCircle, Download, Filter, Calendar } from 'lucide-react';

export default function PayrollManagement() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [payrollSummary, setPayrollSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filter states
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [payPeriodStart, setPayPeriodStart] = useState('');
  const [payPeriodEnd, setPayPeriodEnd] = useState('');

  useEffect(() => {
    loadEmployees();
    
    // Set default pay period (current month)
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setPayPeriodStart(firstDay?.toISOString()?.split('T')?.[0]);
    setPayPeriodEnd(lastDay?.toISOString()?.split('T')?.[0]);
  }, []);

  useEffect(() => {
    if (payPeriodStart && payPeriodEnd) {
      loadSalaryRecords();
      loadPayrollSummary();
    }
  }, [payPeriodStart, payPeriodEnd, departmentFilter]);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await payrollService?.getAllEmployeesWithSalary();
      setEmployees(data);
    } catch (err) {
      setError(err?.message || 'Failed to load employees');
      console.error('Load employees error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSalaryRecords = async () => {
    try {
      setError('');
      const data = await payrollService?.getSalaryRecordsByPeriod(payPeriodStart, payPeriodEnd);
      setSalaryRecords(data);
    } catch (err) {
      setError(err?.message || 'Failed to load salary records');
      console.error('Load salary records error:', err);
    }
  };

  const loadPayrollSummary = async () => {
    try {
      const data = await payrollService?.getPayrollSummary(
        payPeriodStart,
        payPeriodEnd,
        departmentFilter || null
      );
      setPayrollSummary(data);
    } catch (err) {
      console.error('Load payroll summary error:', err);
    }
  };

  const handleSelectEmployee = (employeeId) => {
    setSelectedEmployees(prev => {
      if (prev?.includes(employeeId)) {
        return prev?.filter(id => id !== employeeId);
      }
      return [...prev, employeeId];
    });
  };

  const handleSelectAll = () => {
    const filteredEmployees = getFilteredEmployees();
    if (selectedEmployees?.length === filteredEmployees?.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(filteredEmployees?.map(emp => emp?.id));
    }
  };

  const getFilteredEmployees = () => {
    return employees?.filter(emp => {
      if (departmentFilter && emp?.department !== departmentFilter) {
        return false;
      }
      return true;
    });
  };

  const handleProcessPayroll = async () => {
    if (selectedEmployees?.length === 0) {
      setError('Please select at least one employee');
      return;
    }

    if (!payPeriodStart || !payPeriodEnd) {
      setError('Please select pay period');
      return;
    }

    try {
      setProcessing(true);
      setError('');
      setSuccess('');

      const results = await payrollService?.processBatchPayroll(
        selectedEmployees,
        payPeriodStart,
        payPeriodEnd
      );

      const successCount = results?.filter(r => r?.status === 'processed')?.length;
      const errorCount = results?.filter(r => r?.status === 'error')?.length;
      const skippedCount = results?.filter(r => r?.status === 'skipped')?.length;

      setSuccess(
        `Payroll processing complete: ${successCount} processed, ${skippedCount} skipped, ${errorCount} errors`
      );
      
      setSelectedEmployees([]);
      await loadSalaryRecords();
      await loadPayrollSummary();
    } catch (err) {
      setError(err?.message || 'Failed to process payroll');
      console.error('Process payroll error:', err);
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const uniqueDepartments = [...new Set(employees.map(emp => emp.department))];
  const filteredEmployees = getFilteredEmployees();

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar onToggle={() => {}} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header sidebarCollapsed={false} />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Payroll Management</h1>
              <p className="text-gray-600 mt-1">
                Process salary payments and generate payroll reports
              </p>
            </div>

            {/* Error/Success Messages */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-800">{error}</p>
                </div>
                <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
                  ×
                </button>
              </div>
            )}

            {success && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-green-800">{success}</p>
                </div>
                <button onClick={() => setSuccess('')} className="text-green-600 hover:text-green-800">
                  ×
                </button>
              </div>
            )}

            {/* Payroll Summary Cards */}
            {payrollSummary && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Employees</p>
                      <p className="text-2xl font-bold text-gray-900">{payrollSummary?.totalEmployees}</p>
                    </div>
                    <Users className="w-8 h-8 text-blue-600" />
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Gross Pay</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(payrollSummary?.totalGrossPay)}
                      </p>
                    </div>
                    <IndianRupee className="w-8 h-8 text-green-600" />
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Net Pay</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(payrollSummary?.totalNetPay)}
                      </p>
                    </div>
                    <IndianRupee className="w-8 h-8 text-indigo-600" />
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Average Salary</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(payrollSummary?.averageSalary)}
                      </p>
                    </div>
                    <IndianRupee className="w-8 h-8 text-purple-600" />
                  </div>
                </div>
              </div>
            )}

            {/* Filters and Actions */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Pay Period Start
                  </label>
                  <Input
                    type="date"
                    value={payPeriodStart}
                    onChange={(e) => setPayPeriodStart(e?.target?.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Pay Period End
                  </label>
                  <Input
                    type="date"
                    value={payPeriodEnd}
                    onChange={(e) => setPayPeriodEnd(e?.target?.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Filter className="w-4 h-4 inline mr-1" />
                    Department
                  </label>
                  <select
                    value={departmentFilter}
                    onChange={(e) => setDepartmentFilter(e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Departments</option>
                    {uniqueDepartments?.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <Button
                    onClick={handleProcessPayroll}
                    disabled={processing || selectedEmployees?.length === 0}
                    className="w-full"
                  >
                    {processing ? 'Processing...' : `Process Payroll (${selectedEmployees?.length})`}
                  </Button>
                </div>
              </div>
            </div>

            {/* Employee Selection Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Select Employees for Processing</h2>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedEmployees?.length === filteredEmployees?.length && filteredEmployees?.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Select All</span>
                </label>
              </div>

              {loading ? (
                <div className="p-8 text-center text-gray-500">
                  Loading employees...
                </div>
              ) : filteredEmployees?.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No employees found
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Select</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Base Salary</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredEmployees?.map((employee) => {
                        const hasRecord = salaryRecords?.some(r => r?.employeeId === employee?.employeeId);
                        return (
                          <tr key={employee?.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedEmployees?.includes(employee?.id)}
                                onChange={() => handleSelectEmployee(employee?.id)}
                                className="w-4 h-4 text-blue-600 rounded"
                              />
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">{employee?.employeeId}</td>
                            <td className="px-4 py-3">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{employee?.fullName}</div>
                                <div className="text-sm text-gray-500">{employee?.email}</div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">{employee?.department}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{employee?.position}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                              {formatCurrency(employee?.baseSalary)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {hasRecord ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Processed
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Pending
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Processed Salary Records */}
            {salaryRecords?.length > 0 && (
              <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Processed Salary Records</h2>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Base</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Allowances</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Bonuses</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Gross Pay</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Deductions</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net Pay</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {salaryRecords?.map((record) => (
                        <tr key={record?.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{record?.fullName}</div>
                              <div className="text-sm text-gray-500">{record?.employeeId}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">
                            {formatCurrency(record?.baseSalary)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">
                            {formatCurrency(record?.allowances)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">
                            {formatCurrency(record?.bonuses)}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                            {formatCurrency(record?.grossPay)}
                          </td>
                          <td className="px-4 py-3 text-sm text-red-600 text-right">
                            {formatCurrency(record?.deductions + record?.taxDeductions)}
                          </td>
                          <td className="px-4 py-3 text-sm font-bold text-green-600 text-right">
                            {formatCurrency(record?.netPay)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/salary-slip-generator?employeeId=${record?.employeeId}&start=${payPeriodStart}&end=${payPeriodEnd}`)}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              View Slip
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}